/**
 * The one payload shape the Looker routes return and the page + chat read.
 * Shared by api/ (which builds it) and src/ (which renders it), so the two
 * cannot drift. See docs/superpowers/specs/2026-10-08-looker-dashboard-design.md §4.
 *
 * @module services/looker/lookerTypes
 */

import type { AppWidget } from './lookerAppTiles';
import type { TargetObjective, TargetPlatform } from '../targets/targetRules';
import type { ResultMappingInfo, ResultMetric } from '../targets/resultMetric';

/** Rows the server keeps per tile. Beyond this the tile says "more exist". */
export const LOOKER_TILE_ROW_CAP = 500;

/** Height of Looker's picture of a tile the app does not draw, px. Shared so server and card agree. */
export const LOOKER_RENDER_HEIGHT = 300;

/** Most series a pivoted tile keeps. Beyond this the tile says "more exist". */
export const LOOKER_TILE_SERIES_CAP = 20;

export type LookerTileType =
  | 'single_value'
  | 'record'
  | 'line'
  | 'area'
  | 'bar'
  | 'scatter'
  | 'pie'
  | 'funnel'
  | 'waterfall'
  | 'table'
  /** Not a Looker chart: a box the app draws itself (see LookerTile.app). */
  | 'app';

/** How a series is drawn when a chart mixes them (Looker's `series_types`). */
export type LookerSeriesType = 'line' | 'area' | 'bar' | 'scatter';

/**
 * The parts of Looker's vis_config the app draws. Everything else Looker
 * stores (fonts, tick density, axis ranges, …) is not read. Keys are the
 * tile's field names — for a pivoted measure, the flattened series name.
 */
export interface LookerVis {
  stacking: 'normal' | 'percent' | null;
  /** Series drawn against a right-hand axis. */
  rightAxis: string[];
  /** Validated hex colours only. */
  seriesColors: Record<string, string>;
  seriesTypes: Record<string, LookerSeriesType>;
  valueLabels: boolean;
  /** `looker_bar`: bars run sideways. */
  horizontal: boolean;
  /** Pie with a hole (`looker_donut_multiples`, or `inner_radius` set). */
  donut: boolean;
  /** Single value: how the next field is shown under the main one; null = not shown. */
  comparison: LookerComparison | null;
  /** Table cell rules, in the author's order; the first match styles a cell. */
  conditionalFormats: LookerConditionalFormat[];
  /** The rules style the totals row too. */
  formatTotals: boolean;
  /** Horizontal lines and bands on a chart's value axis, over its first series. */
  referenceLines: LookerReferenceLine[];
}

/** Looker's `comparison_type`. */
export interface LookerComparison {
  type: 'value' | 'change' | 'progress' | 'progress_percentage';
  /** `change` only: a fall is good (green), a rise bad. */
  reverseColors: boolean;
  showLabel: boolean;
  /** The author's own label; null = the comparison field's label. */
  label: string | null;
}

export type LookerFormatTest =
  | 'equal to' | 'not equal to' | 'greater than' | 'less than' | 'between' | 'not between' | 'null' | 'not null';

export interface LookerConditionalFormat {
  test: LookerFormatTest;
  /** One number, two for between / not between, none for null / not null. */
  values: number[];
  /** Hex only. */
  background: string | null;
  font: string | null;
  bold: boolean;
  italic: boolean;
  strike: boolean;
  /** Fields the rule covers; null = every measure. */
  fields: string[] | null;
}

/** A number, or a statistic of the series' values. */
export type LookerReferenceValue = number | 'mean' | 'median' | 'max' | 'min';

export interface LookerReferenceLine {
  kind: 'line' | 'range';
  value: LookerReferenceValue;
  /** The far edge of a range; null for a line. */
  end: LookerReferenceValue | null;
  /** May hold {{mean}} / {{median}} / {{max}} / {{min}}, filled in when drawn. */
  label: string | null;
  color: string | null;
}

export const DEFAULT_LOOKER_VIS: LookerVis = {
  stacking: null,
  rightAxis: [],
  seriesColors: {},
  seriesTypes: {},
  valueLabels: false,
  horizontal: false,
  donut: false,
  comparison: null,
  conditionalFormats: [],
  formatTotals: false,
  referenceLines: [],
};
export type LookerTileError = 'unsupported' | 'failed';

/** A Looker cell: the raw value for charts, Looker's own formatting for text. */
export interface LookerCell {
  value: string | number | null;
  /** Formatted by the LookML value_format; null when Looker sent none. */
  rendered: string | null;
}

export type LookerRow = Record<string, LookerCell>;

export interface LookerField {
  /** Looker field name, e.g. "single_data.spend". Key into LookerRow. */
  name: string;
  label: string;
  kind: 'dimension' | 'measure';
  /** A column the server derived (Share %). */
  role?: 'share';
}

export type LookerTileWidth = 'sixth' | 'third' | 'half' | 'full';

/** Per-tile drawing options the server read from the tile's vis_config.ydm. */
export interface LookerTileOptions {
  /** Table: measures that get a Δ% column next to them. Full field names. */
  compareMeasures: string[];
  /** Table: rows whose `field` is below `value` are drawn as not yet reliable. */
  fadeBelow: { field: string; value: number } | null;
}

/** A table row's identity across the current and previous period: its dimension values. */
export const rowKey = (fields: readonly LookerField[], row: LookerRow): string =>
  fields.filter((f) => f.kind === 'dimension').map((f) => String(row[f.name]?.value ?? '')).join('\u0001');

/** DEC5: one objective's Cost per Result against its approved target. Status comes from targetVerdict, never re-derived. */
export interface TileTargetCell {
  objective: TargetObjective;
  /** null = no target in effect (or not read). */
  target: number | null;
  /**
   * none = no target set; na = no Cost per Result to judge; failed = the targets
   * could not be read; noCurrency = no verdict, the page's currency is unknown or mixed;
   * otherMetric = a target exists, but only for another Result definition (setAgainst): not used, no verdict (DEC3 Q3).
   */
  status: 'met' | 'near' | 'over25' | 'none' | 'na' | 'failed' | 'noCurrency' | 'otherMetric';
  deltaPct: number | null;
  /** The target moved inside the period. */
  changed: { from: number; to: number; on: string } | null;
  /** The channel whose own target was used; null = the objective-level target (or none). */
  platform: TargetPlatform | null;
  /** DEC3: the definition the unused target was set against; only with status 'otherMetric'. */
  setAgainst?: ResultMetric;
}

/** DEC5 targets the server attached to box 9 / box 3 (api/_lib/lookerTargets). */
export interface TileTargets {
  /** R2: the tile's resultThrough, else the period end. */
  asOf: string;
  /** null = no single, certain currency (several, or spend of unknown currency): every cell is noCurrency. */
  currency: string | null;
  /** Set only when every cell with a target used this one filtered channel's own target (the header names it). */
  platform: TargetPlatform | null;
  failed: boolean;
  byObjective: Partial<Record<TargetObjective, TileTargetCell>>;
}

/**
 * DEC3: how Awareness' Result is counted on this tile, and where it shows:
 * 'rows' = on its Awareness rows, if it has any (D1 sends it on boxes 9 and 13
 * whenever a mapping exists; /looker only when a row is Awareness); 'card' = the
 * whole card is one-objective Awareness (box 3); null = no Awareness Result on
 * this tile.
 */
export interface TileResultMetric extends ResultMappingInfo {
  awarenessAt: 'rows' | 'card' | null;
}

export interface LookerTile {
  id: string;
  title: string;
  type: LookerTileType;
  fields: LookerField[];
  rows: LookerRow[];
  /** True when Looker had more rows than LOOKER_TILE_ROW_CAP. */
  truncated: boolean;
  /** True when a pivot had more columns than LOOKER_TILE_SERIES_CAP. */
  seriesTruncated: boolean;
  /**
   * Looker's totals row when the author ticked Totals: over every row the
   * filters match, not only the rows loaded. Absent or null = no totals.
   */
  totals?: LookerRow | null;
  /** The template author gave this tile more than half a row; the page's default is then full width. */
  wide?: boolean;
  /** Width from the template's layout (4/8/12/24 Looker columns). */
  width?: LookerTileWidth;
  /** Single value: the previous period's row; null = asked for but not available. Absent = not compared. */
  previous?: LookerRow | null;
  /** Table: the previous period's rows by rowKey. Absent = not compared. */
  previousByKey?: Record<string, LookerRow>;
  /** Pie: dimension values with spend at some time but none in this range. */
  absent?: string[];
  /** The last day the tile's Result / Cost per Result count (today − 3, D1 decision 4). Absent = the tile has no Result field. */
  resultThrough?: string;
  /**
   * The period the cut Result / Cost per Result Δ is compared with (inclusive).
   * It can differ from the page's period: a cut range is compared with an
   * equal-length one. Absent = the tile is not compared or has no previous period.
   */
  resultPrevious?: { start: string; end: string };
  options?: LookerTileOptions;
  /**
   * Set when type === 'app': the box the app draws from its own data. The tile
   * has no fields or rows (no Looker query was run for it).
   */
  app?: { widget: AppWidget };
  /**
   * The tile's chart type is one the app does not draw (map, timeline, custom
   * visualization…). The page shows Looker's own rendering of it; rows still
   * carry the data, for the table fallback and the chat.
   */
  render?: boolean;
  vis: LookerVis;
  error: LookerTileError | null;
  /** DEC5: Cost per Result targets (box 9 rows, box 3's card). Absent = the tile is not judged against a target. */
  targets?: TileTargets;
  /** DEC3: set only on a tile with a Result field. */
  resultMetric?: TileResultMetric;
}

/**
 * How the page draws a filter: `date` and `channel` get the app's own period
 * select and channel dropdown; anything else stays a free-text Looker expression.
 */
export type LookerFilterKind = 'date' | 'channel' | 'objective' | 'text';

/** The bounded range the date filter resolved to, and the equal period before it (Δ%). */
export interface LookerPeriod {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  days: number;
}

export interface LookerFilter {
  name: string;
  title: string;
  /** Looker filter expression; '' means no filter. */
  value: string;
  kind: LookerFilterKind;
}

export type LookerScope = { kind: 'workspace'; workspaceId: string } | { kind: 'all' };

export interface LookerDashboardPayload {
  template: { id: string; title: string; filters: LookerFilter[] };
  scope: LookerScope;
  freshness: { latestDataDate: string | null };
  /** null/absent = no bounded range (all time, or a date expression the app does not interpret). */
  period?: LookerPeriod | null;
  /**
   * The Objective filter's value when it is exactly one objective (the same one
   * box 3's showWhen reads); null/absent = none or several. The coverage badges read it.
   */
  objective?: string | null;
  /**
   * FR-X-02: currencies with spend in scope, most spend first. More than one =
   * the server forced `currency` onto every query and the page offers a picker.
   */
  currencies: string[];
  /** The currency every number is in when there are several; null = one currency (or none), nothing forced. */
  currency: string | null;
  /** Rows of unknown currency with spend are left out by the forced currency. */
  currencyUnknownExcluded: boolean;
  /** DEC3: the Result definition this load used. Absent = an older server: the central default, no note. */
  resultMapping?: ResultMappingInfo;
  tiles: LookerTile[];
}

export interface LookerTemplateSummary {
  id: string;
  title: string;
  description: string | null;
  /** The template every workspace always has; listed first. */
  isDefault?: boolean;
  /** The dashboard on Looker itself. Only the all-workspaces (master) list carries it. */
  lookerUrl?: string;
  /** Made in the app's tile builder, so it can be edited there. Only the master list carries it. */
  appBuilt?: boolean;
}

/** /api/looker-tile-image: Looker's rendering of one tile, as a PNG. */
export interface LookerTileImage {
  /** Base64 PNG. */
  image: string;
  /** When Looker rendered it (ISO). A cached image says so. */
  renderedAt: string;
}
